/**
 * lp-preview-frame.tsx
 * ────────────────────
 * Desktop / Mobile preview surface for the LP Express in-canvas editor.
 *
 * Why an iframe for mobile?
 * The LP Express renderer is responsive via Tailwind's `sm:`/`md:`/`lg:`
 * breakpoints, which are viewport-width media queries — they fire against the
 * REAL browser window, not the preview container. Simply shrinking a div to
 * 390px would keep the desktop breakpoints active and render a squished desktop
 * layout. An iframe gives the preview its own narrow viewport (390px), so the
 * page's own breakpoints fire exactly as they would on a phone.
 *
 * Why a nested React root (not createPortal)?
 * React's event system attaches its listeners to the document that owns the
 * React root. A `createPortal` into the iframe's document would render the DOM
 * there but native events would never reach the main-document root, so clicks /
 * typing in the in-canvas editor would silently do nothing. Mounting a SECOND
 * React root inside the iframe makes events work, and because <LpExpressRenderer>
 * is fully self-contained (it provides ALL of its own context from props) the
 * editor keeps working without re-wiring any providers across the boundary.
 *
 * The editor primitives in lp-editor.tsx derive their document/window from the
 * rendered node's `ownerDocument` (backward-compatible: that's the main document
 * when not inside an iframe), so popovers, inline text editing and outside-click
 * handling all operate inside the iframe correctly.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { Loader2 } from "lucide-react";

const CLONE_ATTR = "data-lp-style-clone";

// Mirror the main document's stylesheets (and dark-mode root class) into the
// iframe so Tailwind + shadcn + brand fonts render identically. Re-runnable: it
// clears its previous clones first, so a MutationObserver can call it on every
// head change (Vite injects/updates <style> tags during dev HMR).
function syncHeadStyles(srcDoc: Document, destDoc: Document) {
  destDoc.head.querySelectorAll(`[${CLONE_ATTR}]`).forEach((n) => n.remove());
  const nodes = srcDoc.head.querySelectorAll(
    'style, link[rel="stylesheet"], link[rel="preload"][as="font"], link[as="font"]',
  );
  nodes.forEach((n) => {
    const clone = n.cloneNode(true) as HTMLElement;
    clone.setAttribute(CLONE_ATTR, "");
    destDoc.head.appendChild(clone);
  });
  // Carry over the <html> class (e.g. "dark") so theme-token utilities resolve
  // the same way inside the frame as in the host app.
  destDoc.documentElement.className = srcDoc.documentElement.className;
}

/**
 * An isolated viewport iframe that mounts `children` via a nested React root.
 * Re-renders the nested root whenever `children` changes so the live preview
 * stays in sync with parent state (edits, regens, polling).
 */
function MobileViewportFrame({
  width,
  height,
  children,
}: {
  width: number;
  height: number;
  children: React.ReactNode;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const rootRef = useRef<Root | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const didSetup = useRef(false);
  const [ready, setReady] = useState(false);

  const setup = useCallback(() => {
    if (didSetup.current) return;
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) return;
    didSetup.current = true;

    syncHeadStyles(document, doc);
    const obs = new MutationObserver(() => syncHeadStyles(document, doc));
    obs.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
    observerRef.current = obs;

    doc.body.style.margin = "0";
    doc.body.style.background = "#FAFAF7";
    const mount = doc.createElement("div");
    doc.body.appendChild(mount);
    rootRef.current = createRoot(mount);
    setReady(true);
  }, []);

  // Belt-and-suspenders: about:blank may already be loaded by the time React
  // commits, in which case onLoad never fires — so also attempt setup here.
  useEffect(() => {
    setup();
  }, [setup]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      const root = rootRef.current;
      rootRef.current = null;
      didSetup.current = false;
      // Defer unmount out of the commit phase to avoid React's
      // "synchronously unmount a root while rendering" warning.
      if (root) setTimeout(() => root.unmount(), 0);
    };
  }, []);

  useEffect(() => {
    if (ready && rootRef.current) rootRef.current.render(<>{children}</>);
  }, [ready, children]);

  return (
    <div style={{ position: "relative", width, height }}>
      <iframe
        ref={iframeRef}
        onLoad={setup}
        title="Mobile preview"
        style={{ width, height, border: 0, display: "block", background: "#FAFAF7" }}
        data-testid="iframe-lp-mobile-preview"
      />
      {/* Cover the brief blank moment while the iframe boots, mirrors host
          styles, and mounts the nested root, so the frame never flashes empty. */}
      {!ready && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#FAFAF7",
          }}
          data-testid="status-mobile-preview-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

/**
 * Wrap the LP Express renderer to switch between desktop (full editor width —
 * unchanged from today) and a centered phone-width device frame. In-canvas
 * editing works in both modes.
 */
export function LpPreviewSurface({
  mode,
  children,
  mobileWidth = 390,
  mobileHeight = 720,
}: {
  mode: "desktop" | "mobile";
  children: React.ReactNode;
  mobileWidth?: number;
  mobileHeight?: number;
}) {
  if (mode !== "mobile") return <>{children}</>;
  return (
    <div
      className="flex flex-col items-center gap-3 bg-neutral-100/70 py-8"
      data-testid="container-lp-mobile-frame"
    >
      <div className="rounded-[2.25rem] bg-neutral-900 p-2.5 shadow-xl ring-1 ring-black/10">
        <div className="overflow-hidden rounded-[1.75rem] bg-white" style={{ width: mobileWidth }}>
          <MobileViewportFrame width={mobileWidth} height={mobileHeight}>
            {children}
          </MobileViewportFrame>
        </div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="text-mobile-preview-hint">
        Phone view (~{mobileWidth}px) — scroll inside the frame. Editing works here too.
      </p>
    </div>
  );
}
